import type {
  DocumentTypeRef,
  FilterExpression,
  NamedFilterFragmentDefinition,
  NamedFragmentRef,
  QueryDiagnostic,
  QueryJsonValue,
  QuerySessionState,
  QueryValueKind,
} from '@aeppic/query/contracts'

import type { QueryToolbarRuntimeOptions } from './types.js'

interface FragmentExpansionResult {
  session: QuerySessionState
  diagnostics: QueryDiagnostic[]
}

interface ExpressionExpansionResult {
  expression?: FilterExpression
  diagnostics: QueryDiagnostic[]
}

type FragmentResolver = NonNullable<QueryToolbarRuntimeOptions['resolveNamedFragment']>

export async function expandSessionFragments(
  session: QuerySessionState,
  resolver: FragmentResolver | undefined,
  maxDepth: number,
  signal?: AbortSignal,
): Promise<FragmentExpansionResult> {
  const cache = new Map<string, NamedFilterFragmentDefinition | null>()
  const entries: Array<{ key: 'search' | 'toolbarFilter' | string, expression: FilterExpression | null, path: readonly (string | number)[] }> = [
    { key: 'search', expression: session.search, path: ['search'] },
    { key: 'toolbarFilter', expression: session.toolbarFilter, path: ['toolbarFilter'] },
    ...Object.keys(session.contributions).sort().map(key => ({ key, expression: session.contributions[key], path: ['contributions', key] })),
  ]
  const expanded = { ...session, contributions: { ...session.contributions } }
  const diagnostics: QueryDiagnostic[] = []

  for (const entry of entries) {
    if (!entry.expression) continue
    const result = await expandExpression(entry.expression, session.documentTypes, resolver, cache, [], 0, maxDepth, entry.path, signal)
    diagnostics.push(...result.diagnostics)
    if (!result.expression) continue
    if (entry.key === 'search') expanded.search = result.expression
    else if (entry.key === 'toolbarFilter') expanded.toolbarFilter = result.expression
    else expanded.contributions[entry.key] = result.expression
  }

  return { session: expanded, diagnostics }
}

async function expandExpression(
  expression: FilterExpression,
  documentTypes: readonly DocumentTypeRef[],
  resolver: FragmentResolver | undefined,
  cache: Map<string, NamedFilterFragmentDefinition | null>,
  stack: readonly string[],
  depth: number,
  maxDepth: number,
  path: readonly (string | number)[],
  signal?: AbortSignal,
): Promise<ExpressionExpansionResult> {
  throwIfAborted(signal)
  if (expression.kind === 'group') {
    const children: FilterExpression[] = []
    const diagnostics: QueryDiagnostic[] = []
    for (let index = 0; index < expression.children.length; index++) {
      const result = await expandExpression(expression.children[index], documentTypes, resolver, cache, stack, depth, maxDepth, [...path, 'children', index], signal)
      diagnostics.push(...result.diagnostics)
      if (result.expression) children.push(result.expression)
    }
    return { expression: { ...expression, children }, diagnostics }
  }
  if (expression.kind === 'relation') {
    const result = await expandExpression(expression.expression, expression.targetTypes?.length ? expression.targetTypes : documentTypes, resolver, cache, stack, depth, maxDepth, [...path, 'expression'], signal)
    return result.expression ? { expression: { ...expression, expression: result.expression }, diagnostics: result.diagnostics } : result
  }
  if (expression.kind !== 'named-fragment') return { expression, diagnostics: [] }

  const key = fragmentKey(expression.fragment)
  if (stack.includes(key)) {
    return failure('recursive-fragment', `Named fragment ${key} recursively references itself`, path)
  }
  if (depth >= maxDepth) {
    return failure('fragment-depth-exceeded', `Named fragment expansion exceeds maximum depth ${maxDepth}`, path)
  }
  if (!resolver) {
    return failure('unknown-fragment', `No resolver is configured for named fragment ${key}`, path)
  }

  let definition = cache.get(key)
  if (definition === undefined) {
    definition = await resolver(expression.fragment, signal)
    throwIfAborted(signal)
    cache.set(key, definition)
  }
  if (!definition) {
    return failure('unknown-fragment', `Named fragment ${key} was not found`, path)
  }
  if (fragmentKey(definition.fragment) !== key) {
    return failure('fragment-version-mismatch', `Named fragment resolver returned ${fragmentKey(definition.fragment)} for ${key}`, [...path, 'fragment'])
  }
  if (!compatibleTypes(documentTypes, definition.documentTypes)) {
    return failure('fragment-document-type-mismatch', `Named fragment ${key} is not compatible with the current document types`, path)
  }

  const parameterDiagnostics = validateParameters(definition, expression.parameters ?? {}, path)
  if (parameterDiagnostics.length > 0) return { diagnostics: parameterDiagnostics }
  const substituted = substituteParameters(definition.expression, expression.parameters ?? {}, path)
  if (substituted.diagnostics.length > 0 || !substituted.value) return { diagnostics: substituted.diagnostics }

  return expandExpression(substituted.value as FilterExpression, documentTypes, resolver, cache, [...stack, key], depth + 1, maxDepth, path, signal)
}

function validateParameters(definition: NamedFilterFragmentDefinition, supplied: Readonly<Record<string, QueryJsonValue>>, path: readonly (string | number)[]): QueryDiagnostic[] {
  const declared = definition.parameters ?? {}
  const diagnostics: QueryDiagnostic[] = []
  for (const [name, kind] of Object.entries(declared)) {
    if (!Object.prototype.hasOwnProperty.call(supplied, name)) {
      diagnostics.push(diagnostic('missing-fragment-parameter', `Named fragment requires parameter ${name}`, [...path, 'parameters', name]))
    } else if (!matchesKind(supplied[name], kind)) {
      diagnostics.push(diagnostic('invalid-fragment-parameter', `Named fragment parameter ${name} must be ${kind}`, [...path, 'parameters', name]))
    }
  }
  for (const name of Object.keys(supplied)) {
    if (!Object.prototype.hasOwnProperty.call(declared, name)) {
      diagnostics.push(diagnostic('unknown-fragment-parameter', `Named fragment does not declare parameter ${name}`, [...path, 'parameters', name]))
    }
  }
  return diagnostics
}

function substituteParameters(value: unknown, supplied: Readonly<Record<string, QueryJsonValue>>, path: readonly (string | number)[]): { value?: unknown, diagnostics: QueryDiagnostic[] } {
  if (Array.isArray(value)) {
    const values: unknown[] = []
    const diagnostics: QueryDiagnostic[] = []
    value.forEach((child, index) => {
      const result = substituteParameters(child, supplied, [...path, index])
      diagnostics.push(...result.diagnostics)
      if (result.value !== undefined) values.push(result.value)
    })
    return { value: values, diagnostics }
  }
  if (!value || typeof value !== 'object') return { value, diagnostics: [] }
  const objectValue = value as Record<string, unknown>
  if (Object.keys(objectValue).length === 1 && typeof objectValue.$parameter === 'string') {
    return Object.prototype.hasOwnProperty.call(supplied, objectValue.$parameter)
      ? { value: cloneJson(supplied[objectValue.$parameter]), diagnostics: [] }
      : { diagnostics: [diagnostic('missing-fragment-parameter', `No value was supplied for fragment parameter ${objectValue.$parameter}`, path)] }
  }

  const result: Record<string, unknown> = {}
  const diagnostics: QueryDiagnostic[] = []
  for (const [key, child] of Object.entries(objectValue)) {
    const expanded = substituteParameters(child, supplied, [...path, key])
    diagnostics.push(...expanded.diagnostics)
    if (expanded.value !== undefined) result[key] = expanded.value
  }
  return { value: result, diagnostics }
}

function matchesKind(value: QueryJsonValue, kind: QueryValueKind): boolean {
  if (Array.isArray(value)) return value.length > 0 && value.every(item => matchesKind(item, kind))
  if (value === null || typeof value === 'object') return kind === 'unknown'
  if (kind === 'number' || kind === 'boolean') return typeof value === kind
  if (kind === 'unknown') return true
  return typeof value === 'string'
}

function compatibleTypes(actual: readonly DocumentTypeRef[], expected: readonly DocumentTypeRef[]): boolean {
  return actual.length > 0 && actual.every(type => expected.some(candidate => candidate.id === type.id && (!candidate.version || !type.version || candidate.version === type.version)))
}

function fragmentKey(fragment: NamedFragmentRef): string {
  return `${fragment.id}@${fragment.version}`
}

function failure(code: string, message: string, path: readonly (string | number)[]): ExpressionExpansionResult {
  return { diagnostics: [diagnostic(code, message, path)] }
}

function diagnostic(code: string, message: string, path: readonly (string | number)[]): QueryDiagnostic {
  return { code, message, severity: 'error', path }
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value))
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new Error('Query toolbar operation aborted')
}
